import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Filter, 
  TrendingUp, 
  Calendar,
  Building2,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';
import { formatINR, formatCommission } from '@/utils/currency';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';

// Group commissions by deal
function groupByDeal(commissions) {
  const grouped = {};
  commissions.forEach(comm => {
    const dealId = comm.dealId || 'unknown';
    if (!grouped[dealId]) {
      grouped[dealId] = {
        dealId: dealId,
        dealName: comm.dealName || 'Unknown Deal',
        location: comm.location || '',
        city: comm.city || '',
        transactions: [],
        totalCapital: 0,
        totalCommission: 0,
        paidCommission: 0,
        pendingCommission: 0
      };
    }
    // Map commission to transaction format for display
    grouped[dealId].transactions.push({
      id: comm.commissionId,
      date: comm.bookedAt || comm.investmentDate || comm.createdAt,
      investorName: comm.investorName || 'Unknown',
      investorId: comm.investorId,
      tier: comm.tierAtInvestment || 'Bronze',
      capitalRaised: comm.capitalRaised || 0,
      commissionRate: comm.rateApplied || comm.rateOnCapital || 0,
      commissionAmount: comm.commissionAmount || 0,
      status: comm.status || 'pending',
      outflowStatus: comm.outflowStatus
    });
    grouped[dealId].totalCapital += comm.capitalRaised || 0;
    grouped[dealId].totalCommission += comm.commissionAmount || 0;
    if (comm.status === 'paid') {
      grouped[dealId].paidCommission += comm.commissionAmount || 0;
    } else {
      grouped[dealId].pendingCommission += comm.commissionAmount || 0;
    }
  });
  return Object.values(grouped);
}

function DealLedgerCard({ dealData, isExpanded, onToggle }) {
  const tierColors = {
    Bronze: 'text-orange-400',
    Silver: 'text-gray-300',
    Gold: 'text-yellow-400',
    Platinum: 'text-cyan-400',
    Diamond: 'text-purple-400'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden"
    >
      {/* Deal Header */}
      <button
        onClick={onToggle}
        className="w-full p-5 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-[#4ADE80]/10 rounded-sm">
            <Building2 className="w-5 h-5 text-[#4ADE80]" />
          </div>
          <div className="text-left">
            <h3 className="text-white font-medium">{dealData.dealName}</h3>
            <p className="text-sm text-white/40">
              {dealData.location}{dealData.city ? `, ${dealData.city}` : ''}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-xs text-white/40 uppercase tracking-wider">Total Commission</p>
            <p className="text-xl font-mono text-[#4ADE80]">{formatCommission(dealData.totalCommission)}</p>
          </div>
          <div className="text-right hidden md:block">
            <p className="text-xs text-white/40 uppercase tracking-wider">Capital Raised</p>
            <p className="text-lg font-mono text-white">{formatINR(dealData.totalCapital)}</p>
          </div>
          <div className="text-right hidden md:block">
            <p className="text-xs text-white/40 uppercase tracking-wider">Transactions</p>
            <p className="text-lg font-mono text-white">{dealData.transactions.length}</p>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-white/40" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/40" />
          )}
        </div>
      </button>

      {/* Status Summary */}
      <div className="px-5 pb-3 flex items-center gap-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-500" />
          <span className="text-sm text-white/50">Paid:</span>
          <span className="text-sm font-mono text-green-500">{formatCommission(dealData.paidCommission)}</span>
        </div>
        {dealData.pendingCommission > 0 && (
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-500" />
            <span className="text-sm text-white/50">Pending:</span>
            <span className="text-sm font-mono text-yellow-500">{formatCommission(dealData.pendingCommission)}</span>
          </div>
        )}
      </div>

      {/* Expanded Transactions */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-white/5"
        >
          <div className="p-4">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-white/40 uppercase tracking-wider">
                  <th className="pb-3">Date</th>
                  <th className="pb-3">Investor</th>
                  <th className="pb-3 text-right">Capital</th>
                  <th className="pb-3 text-right">Rate</th>
                  <th className="pb-3 text-right">Commission</th>
                  <th className="pb-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {dealData.transactions.map((tx) => (
                  <tr key={tx.id} className="border-t border-white/5">
                    <td className="py-3 text-sm text-white/60 font-mono">
                      {tx.date ? new Date(tx.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                    <td className="py-3">
                      <p className="text-sm text-white">{tx.investorName || 'Unknown'}</p>
                      <p className={`text-xs ${tierColors[tx.tier] || 'text-white/40'}`}>{tx.tier || 'Bronze'} Tier</p>
                    </td>
                    <td className="py-3 text-right text-sm font-mono text-white">
                      {formatINR(tx.capitalRaised || 0)}
                    </td>
                    <td className="py-3 text-right text-sm font-mono text-white/60">
                      {(tx.commissionRate || 0).toFixed(2)}%
                    </td>
                    <td className="py-3 text-right text-sm font-mono text-[#4ADE80]">
                      {formatCommission(tx.commissionAmount || 0)}
                    </td>
                    <td className="py-3 text-right">
                      {tx.status === 'paid' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 border border-green-500/30 rounded-sm text-xs text-green-500">
                          <CheckCircle2 className="w-3 h-3" />
                          Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded-sm text-xs text-yellow-500">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function BrokerLedger() {
  const [commissions, setCommissions] = useState([]);
  const [totals, setTotals] = useState({
    totalCommission: 0,
    totalCapital: 0,
    paidCommission: 0,
    pendingCommission: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedDeals, setExpandedDeals] = useState([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchCommissions();
  }, [statusFilter]);

  const fetchCommissions = async () => {
    setLoading(true);
    setError(null);
    try {
      let brokerId = user.brokerId;
      if (!brokerId) {
        try {
          const profileRes = await api.get(`/broker/status/${user.id}`);
          brokerId = profileRes.data.brokerId;
        } catch { /* ignore */ }
      }
      
      const effectiveId = brokerId || user.id;

      // Fetch ledger data AND centralized metrics in parallel
      const [ledgerRes, metricsRes] = await Promise.all([
        api.get(`/broker/${effectiveId}/commission-ledger`),
        api.get(`/metrics/broker/${effectiveId}`)
      ]);

      const ledger = ledgerRes.data.ledger || [];
      
      // Transform to commission format expected by groupByDeal
      const transformed = ledger.map(l => ({
        commissionId: l.investmentId,
        dealId: l.unitId,
        dealName: `${l.projectName} - ${l.unitName}`,
        location: '',
        city: '',
        investorName: l.investorName,
        investorId: l.investorId,
        capitalRaised: l.capitalPaid || l.capitalCommitted || 0,
        commissionAmount: l.commission || 0,
        rateApplied: l.capitalCommitted > 0 ? ((l.commission / l.capitalCommitted) * 100) : 0,
        tierAtInvestment: l.tier || 'Bronze',
        status: l.state === 'COMPLETED' || l.state === 'CAPITAL_PAID' ? 'paid' : 'pending',
        createdAt: l.createdAt,
      }));
      
      setCommissions(statusFilter === 'all' ? transformed : transformed.filter(c => c.status === statusFilter));
      
      // Use centralized metrics for summary totals (SSoT)
      const metricsData = metricsRes.data;
      setTotals({
        totalCommission: metricsData.totalCommission || 0,
        totalCapital: metricsData.capitalCommitted || 0,
        paidCommission: metricsData.paidCommission || 0,
        pendingCommission: metricsData.pendingCommission || 0,
      });
    } catch (err) {
      console.error('Error fetching commissions:', err);
      setError('Failed to load commission data.');
    } finally {
      setLoading(false);
    }
  };

  const groupedDeals = groupByDeal(commissions);

  const toggleDeal = (dealId) => {
    setExpandedDeals(prev => 
      prev.includes(dealId) 
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  return (
    <div className="min-h-screen bg-[#030303] flex">
      <BrokerSidebar />

      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8">
        {error && <ErrorBanner message={error} onRetry={fetchCommissions} />}
        {loading ? <DashboardSkeleton cards={4} rows={5} /> : (<>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-2">
              Commission Ledger
            </h1>
            <p className="text-white/50 text-sm">Track your earnings across all deals</p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-sm text-[#4ADE80] text-sm hover:bg-[#4ADE80]/20 transition-colors">
            <Download className="w-4 h-4" />
            Export Statement
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-[#4ADE80]" />
              <span className="text-xs text-white/40 uppercase tracking-wider">Total Commission</span>
            </div>
            <p className="text-2xl font-mono text-[#4ADE80]">{formatCommission(totals.totalCommission)}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-xs text-white/40 uppercase tracking-wider">Paid Out</span>
            </div>
            <p className="text-2xl font-mono text-green-500">{formatCommission(totals.paidCommission)}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-yellow-500" />
              <span className="text-xs text-white/40 uppercase tracking-wider">Pending</span>
            </div>
            <p className="text-2xl font-mono text-yellow-500">{formatCommission(totals.pendingCommission)}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-white/40" />
              <span className="text-xs text-white/40 uppercase tracking-wider">Capital Raised</span>
            </div>
            <p className="text-2xl font-mono text-white">{formatINR(totals.totalCapital)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2 text-white/50">
            <Filter className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">Filter:</span>
          </div>
          <div className="flex gap-2">
            {['all', 'paid', 'pending'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
                  statusFilter === status
                    ? 'bg-[#4ADE80]/10 border-[#4ADE80]/30 text-[#4ADE80]'
                    : 'bg-transparent border-white/10 text-white/50 hover:border-white/20'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Deal Ledgers */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
            </div>
          ) : groupedDeals.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A] border border-white/10 rounded-sm">
              <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No commission records found</p>
              <p className="text-white/30 text-sm mt-2">Start referring investors to earn commissions</p>
            </div>
          ) : (
            groupedDeals.map((dealData) => (
              <DealLedgerCard
                key={dealData.dealId}
                dealData={dealData}
                isExpanded={expandedDeals.includes(dealData.dealId)}
                onToggle={() => toggleDeal(dealData.dealId)}
              />
            ))
          )}
        </div>
        </>)}
      </main>
    </div>
  );
}

